import styled, { css, DefaultTheme } from 'styled-components';

import {
  BUTTON_SIZES,
  BUTTON_SPEC,
  ButtonColors,
  ButtonSize,
  ButtonType,
  buttonFocusRing,
} from './spec';

const BORDER_WIDTH = 1;
// The width of the global focus outline (src/styles/global), kept for forced-colors mode.
const FORCED_COLORS_OUTLINE_WIDTH = 2;

const colors = (theme: DefaultTheme, { background, text, border }: ButtonColors) => css`
  background-color: ${background === 'transparent' ? 'transparent' : theme[background]};
  color: ${theme[text]};
  border-color: ${border ? theme[border] : 'transparent'};
`;

export const StyledButton = styled.button<{
  $variant: ButtonType;
  $size: ButtonSize;
  $iconOnly: boolean;
}>`
  ${({ theme, $variant, $size, $iconOnly }) => {
    const size = BUTTON_SIZES[$size];
    const type = theme.typography[size.fontStyle];
    const spec = BUTTON_SPEC[$variant];

    return css`
      box-sizing: border-box;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      column-gap: ${size.gap}px;
      height: ${size.height}px;
      width: ${$iconOnly ? `${size.height}px` : 'auto'};
      /* Figma draws the stroke inside the box, so the border takes its width from the padding. */
      padding: 0 ${$iconOnly ? 0 : size.paddingX - BORDER_WIDTH}px;
      border: ${BORDER_WIDTH}px solid;
      border-radius: ${size.radius}px;
      font-family: ${type.fontFamily};
      font-size: ${type.fontSize};
      line-height: ${type.lineHeight};
      font-weight: ${type.fontWeight};
      letter-spacing: ${type.letterSpacing};
      white-space: nowrap;
      cursor: pointer;
      ${colors(theme, spec.default)}

      &:hover:not(:disabled) {
        ${colors(theme, spec.hover)}
      }

      /* Pressed, and also a button whose menu or panel is open or a toggle that is on: those keep
         the Active look of their type (dhcs-dash-client DI-016, answered in DS Base 14087:52925).
         They read the ARIA state screen readers announce, so there is no separate prop. */
      &:active:not(:disabled),
      &[aria-expanded='true']:not(:disabled),
      &[aria-pressed='true']:not(:disabled) {
        ${colors(theme, spec.active)}
      }

      /* Forced colours replace the fills, so an open or on button keeps a border of its own. */
      @media (forced-colors: active) {
        &[aria-expanded='true'],
        &[aria-pressed='true'] {
          border-color: Highlight;
        }
      }

      &:disabled {
        cursor: not-allowed;
        ${colors(theme, spec.disabled)}
      }

      /* The ring is a shadow outside the box, so the border never changes; the transparent
         outline is what forced-colors mode paints once it drops the shadow. */
      &:focus-visible {
        outline: ${FORCED_COLORS_OUTLINE_WIDTH}px solid transparent;
        box-shadow: ${buttonFocusRing(theme)};
      }
    `;
  }}
`;

export const IconSlot = styled.span<{ $size: ButtonSize }>`
  display: inline-flex;
  flex-shrink: 0;
  width: ${({ $size }) => BUTTON_SIZES[$size].iconSize}px;
  height: ${({ $size }) => BUTTON_SIZES[$size].iconSize}px;

  > svg {
    width: 100%;
    height: 100%;
  }
`;
